import React, { ReactNode, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useField, FieldHookConfig } from 'formik';
import {
  Box as Wrapper,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SearchInput,
  Text,
} from '@tonic-ui/react';
import { FieldTextLabel, HelperText, InlineError } from './index';
import { Trans } from 'react-i18next';
import { isGovCloudRegion } from '../../utils/helpers';

type MenuOptionsType = { id: string; name?: any };

type DropdownMenuProps = FieldHookConfig<string | number | boolean | ReactNode> & {
  options: MenuOptionsType[];
  label: string;
  helperText?: string;
  hasRegions?: boolean;
  placeholder: string;
  isRequired: boolean;
  variant?: string;
  isSearchable?: boolean;
  maxHeight: number;
  id?: string;
  onChange?: (id: string) => void;
};

const DropdownMenu = ({
  options = [],
  label,
  helperText = '',
  placeholder = '',
  variant = 'secondary',
  hasRegions = false,
  isRequired = false,
  isSearchable = false,
  maxHeight = 300,
  onChange,
  ...rest
}: DropdownMenuProps): JSX.Element => {
  const { t } = useTranslation();
  const [field, meta, helpers] = useField(rest);
  const [menuItem, setMenuItem] = useState(field.value);
  const [results, setResults] = useState<MenuOptionsType[]>([]);
  const [keyword, setKeyword] = useState('');
  const inputRef = useRef<HTMLInputElement>();
  const isInvalid = meta.error && meta.touched ? meta.error : '';
  const { setValue } = helpers;

  const handleClick = (id, name) => {
    if (id !== '' || name !== '') {
      setValue(id);
      setMenuItem(name);
      if (onChange) onChange(id);
    }
  };

  const getDisplayText = ({ id, name }: MenuOptionsType) => {
    return hasRegions ? `${name} (${id.toLowerCase()})` : name;
  };

  // Specific use-case to check for TREND-US-1 region
  const defaultRegion = options?.find(({ id }) => id === 'trend-us-1' || id === 'gov-us-1');

  const hasTrendRegion = defaultRegion?.id === 'trend-us-1';

  useEffect(() => {
    if (menuItem === '') {
      if (defaultRegion) {
        setValue(defaultRegion.id);
        setMenuItem(getDisplayText(defaultRegion));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultRegion, menuItem]);

  const handleClear = () => {
    const keyword = '';
    setKeyword(keyword);
    filterResults(keyword);
  };

  const handleChange = (e) => {
    const keyword = e.target.value;
    setKeyword(keyword);
    filterResults(keyword);
  };

  const handleFocus = () => {
    if (inputRef.current) inputRef.current.focus();
  };

  const filterResults = (keyword = '') => {
    setResults(
      keyword
        ? options.filter((option) => option.name?.toLowerCase().includes(keyword.toLowerCase()))
        : options
    );
  };

  useEffect(() => {
    if (inputRef.current && document.activeElement?.id !== inputRef.current) {
      inputRef.current.focus();
    }
  }, [keyword]);

  useEffect(() => {
    setResults(options);
  }, [options]);

  const dropdownId = rest?.id || field?.name;

  return (
    <Wrapper>
      {label && <FieldTextLabel name={label} isRequired={isRequired} htmlFor={dropdownId} />}
      <Menu display="block">
        <MenuButton
          id={dropdownId}
          data-id={dropdownId}
          height={30}
          variant={variant}
          fontWeight={300}
          onClick={handleFocus}
          width="100%"
          style={{
            paddingRight: '15px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {field?.value !== '' ? menuItem : placeholder}
        </MenuButton>
        <MenuList
          width="100%"
          whiteSpace="nowrap"
          maxHeight={maxHeight}
          overflow="auto"
          fontWeight={300}
          pt={isSearchable ? '0x' : '2x'}
        >
          {isSearchable ? (
            <Wrapper
              padding="2x"
              style={{
                position: 'sticky',
                top: '0px',
                backgroundColor: 'white',
                zIndex: '1',
              }}
            >
              <SearchInput
                inputProps={{ role: 'menuitem' }}
                value={keyword}
                ref={inputRef}
                onChange={handleChange}
                onClearInput={handleClear}
                onFocus={() => {
                  inputRef?.current?.focus();
                }}
                placeholder={t('inputPlaceholder')}
              />
            </Wrapper>
          ) : (
            ''
          )}
          {results?.length === 0 ? (
            <Text px="4x" py="2x">
              {t('noMatch')}
            </Text>
          ) : (
            results?.map(({ id, name }, i: number) => {
              const displayText = getDisplayText({ id, name }) as string;
              const value = (hasRegions ? id : name) as string;
              return (
                <MenuItem
                  key={`${i}`}
                  id={id}
                  data-id={id}
                  onKeyDown={() => handleClick(id, displayText)}
                  onClick={() => handleClick(id, displayText)}
                  value={value}
                >
                  {displayText}
                </MenuItem>
              );
            })
          )}
        </MenuList>
      </Menu>
      {isInvalid && (
        <Text size="xs" mt="1x">
          <InlineError errorName={isInvalid} />
        </Text>
      )}
      {helperText && !hasTrendRegion && <HelperText value={helperText} />}
      {hasTrendRegion && !isGovCloudRegion && (
        <Wrapper marginTop="5px">
          <Text size="xs" lineHeight="xs">
            <Trans i18nKey="trendRegionNote" />
          </Text>
        </Wrapper>
      )}
    </Wrapper>
  );
};

DropdownMenu.displayName = 'DropdownMenu';

export default DropdownMenu;
